import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    Row,
    Col,
    Card,
} from "react-bootstrap";

import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Skeleton } from "primereact/skeleton";

import { GiTwoCoins } from "react-icons/gi";
import { MdOutlineFileDownload } from "react-icons/md";
import { BsGift } from "react-icons/bs";

import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import { useAuth } from "../../context/AuthContext";

import api from "../../api/axios";

import "../../assets/css/Useraccount.css";


const RedeemOffers = () => {

    const navigate = useNavigate();

    const {
        isAuthenticated,
        tierlevel,
    } = useAuth();


    /* =====================================================
       TIER CONFIG
       ===================================================== */

    const TIER_NAMES = {
        1: "Starter",
        2: "Premium",
        3: "Premium Plus",
        4: "Pro",
    };


    const getTierName = (tier) => {
        return TIER_NAMES[Number(tier)] || `Tier ${tier}`;
    };


    const getTierLabel = (tier) => {
        const tierNumber = Number(tier);

        return `Tier ${tierNumber} · ${getTierName(tierNumber)}`;
    };


    /* =====================================================
       STATE
       ===================================================== */

    const [loading, setLoading] =
        useState(true);

    const [offers, setOffers] =
        useState([]);

    const [
        hasActiveSubscription,
        setHasActiveSubscription,
    ] = useState(false);

    const [
        customerTier,
        setCustomerTier,
    ] = useState(null);

    const [
        subscriptionName,
        setSubscriptionName,
    ] = useState("");

    const [
        redeemingId,
        setRedeemingId,
    ] = useState(null);

    const [
        downloadingId,
        setDownloadingId,
    ] = useState(null);


    /* =====================================================
       COMMON ERROR
       ===================================================== */

    const showError = (
        error,
        fallbackMessage
    ) => {

        toast.error(
            error?.response?.data?.message ||
            fallbackMessage
        );

    };


    /* =====================================================
       FETCH ALL ACTIVE OFFERS
       ===================================================== */

    const fetchOffers =
        useCallback(async () => {

            try {

                setLoading(true);

                const response =
                    await api.get(
                        "/CustomerRedeemRoutes/customer/offers"
                    );


                if (
                    !response.data?.success
                ) {

                    setOffers([]);
                    setHasActiveSubscription(false);
                    setCustomerTier(null);
                    setSubscriptionName("");

                    return;
                }


                const {
                    has_active_subscription,
                    tier_level,
                    subscription_name,
                    data,
                } = response.data;


                setHasActiveSubscription(
                    Boolean(
                        has_active_subscription
                    )
                );


                setCustomerTier(
                    tier_level !== undefined &&
                    tier_level !== null
                        ? Number(tier_level)
                        : null
                );


                setSubscriptionName(
                    subscription_name || ""
                );


                /*
                 * Show ALL active offers.
                 * Do not filter by tier here.
                 */

                setOffers(
                    Array.isArray(data)
                        ? data
                        : []
                );


            } catch (error) {

                console.error(
                    "Redeem offer fetch error:",
                    error
                );


                setOffers([]);


                if (
                    error?.response?.status === 401
                ) {

                    toast.warning(
                        "Please login to view redeem offers."
                    );

                    return;
                }


                showError(
                    error,
                    "Unable to fetch redeem offers."
                );


            } finally {

                setLoading(false);

            }

        }, []);


    /* =====================================================
       INITIAL LOAD
       ===================================================== */

    useEffect(() => {

        if (!isAuthenticated) {

            setLoading(false);

            return;
        }


        fetchOffers();

    }, [
        isAuthenticated,
        fetchOffers,
    ]);


    /* =====================================================
       REDEEM OFFER
       ===================================================== */

    const redeemOffer =
        async (offer) => {

            if (
                !hasActiveSubscription
            ) {

                toast.warning(
                    "An active subscription is required to redeem this offer."
                );

                return;
            }


            if (
                offer.already_redeemed
            ) {

                toast.info(
                    "You have already redeemed this offer."
                );

                return;
            }


            const offerTier =
                Number(
                    offer.applicable_tier
                );


            const activeTier =
                Number(
                    customerTier ??
                    tierlevel
                );


            if (
                offerTier !== activeTier
            ) {

                toast.warning(
                    `This offer is available only for ${getTierName(
                        offerTier
                    )} subscribers.`
                );

                return;
            }


            try {

                setRedeemingId(
                    offer.redeem_offer_id
                );


                const response =
                    await api.post(
                        `/CustomerRedeemRoutes/customer/redeem/${offer.redeem_offer_id}`
                    );


                if (
                    !response.data?.success
                ) {

                    throw new Error(
                        response.data?.message ||
                        "Unable to redeem offer"
                    );

                }


                const redeemData =
                    response.data.data;


                /* =================================================
                   SUCCESS MESSAGE
                   ================================================= */

                let successMessage =
                    "Offer redeemed successfully.";


                if (
                    Number(
                        redeemData?.coins_added
                    ) > 0 &&
                    redeemData?.has_zip
                ) {

                    successMessage =
                        `${redeemData.coins_added} credit coins were added and your ZIP is ready to download.`;

                } else if (
                    Number(
                        redeemData?.coins_added
                    ) > 0
                ) {

                    successMessage =
                        `${redeemData.coins_added} credit coins have been added to your account.`;

                } else if (
                    redeemData?.has_zip
                ) {

                    successMessage =
                        "Your asset pack has been redeemed successfully.";

                }


                toast.success(
                    successMessage
                );


                /* =================================================
                   UPDATE CARD IMMEDIATELY
                   ================================================= */

                setOffers(
                    (previous) =>
                        previous.map(
                            (item) =>
                                item.redeem_offer_id ===
                                offer.redeem_offer_id

                                    ? {
                                        ...item,

                                        already_redeemed:
                                            true,

                                        can_redeem:
                                            false,

                                        is_active:
                                            redeemData
                                                ?.offer_still_active ??
                                            item.is_active,
                                    }

                                    : item
                        )
                );


                /* =================================================
                   AUTO DOWNLOAD ZIP
                   ================================================= */

                if (
                    redeemData?.download_url
                ) {

                    window.location.href =
                        redeemData.download_url;

                }


                await fetchOffers();


            } catch (error) {

                console.error(
                    "Redeem failed:",
                    error
                );


                if (
                    error?.response?.status ===
                    409
                ) {

                    toast.info(
                        error?.response?.data?.message ||
                        "You have already redeemed this offer."
                    );

                    await fetchOffers();

                    return;
                }


                if (
                    error?.response?.status ===
                    403
                ) {

                    toast.warning(
                        error?.response?.data?.message ||
                        "You are not eligible to redeem this offer."
                    );

                    return;
                }


                showError(
                    error,
                    "Unable to redeem this offer."
                );


            } finally {

                setRedeemingId(null);

            }

        };


    /* =====================================================
       DOWNLOAD REDEEMED ZIP AGAIN
       ===================================================== */

    const downloadRedeemedZip =
        async (offer) => {

            try {

                setDownloadingId(
                    offer.redeem_offer_id
                );


                const response =
                    await api.get(
                        `/CustomerRedeemRoutes/customer/redeem-download/${offer.redeem_offer_id}`
                    );


                const downloadUrl =
                    response.data?.data
                        ?.download_url;


                if (!downloadUrl) {

                    throw new Error(
                        "Download link unavailable"
                    );

                }


                window.location.href =
                    downloadUrl;


            } catch (error) {

                console.error(
                    "Redeem ZIP download failed:",
                    error
                );


                showError(
                    error,
                    "Unable to download the offer ZIP."
                );


            } finally {

                setDownloadingId(null);

            }

        };


    /* =====================================================
       OFFER TYPE LABEL
       ===================================================== */

    const offerTypeLabel =
        (type) => {

            switch (type) {

                case "COINS":
                    return "Credit Coins";

                case "ZIP":
                    return "Asset Pack";

                case "COINS_AND_ZIP":
                    return "Coins + Asset Pack";

                default:
                    return "Offer";

            }

        };


    /* =====================================================
       OFFER TYPE SEVERITY
       ===================================================== */

    const offerTypeSeverity =
        (type) => {

            switch (type) {

                case "COINS":
                    return "warning";

                case "ZIP":
                    return "info";

                case "COINS_AND_ZIP":
                    return "success";

                default:
                    return "secondary";

            }

        };


    /* =====================================================
       DATE FORMAT
       ===================================================== */

    const formatExpiry =
        (value) => {

            if (!value) {

                return "No expiry";

            }


            const date =
                new Date(value);


            if (
                Number.isNaN(
                    date.getTime()
                )
            ) {

                return "No expiry";

            }


            return date.toLocaleDateString(
                "en-IN",
                {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                }
            );

        };


    /* =====================================================
       LOADING SKELETON
       ===================================================== */

    const OfferSkeleton = () => {

        return (

            <Row className="g-4">

                {[1, 2, 3].map(
                    (item) => (

                        <Col
                            xl={4}
                            lg={4}
                            md={6}
                            xs={12}
                            key={item}
                        >

                            <Card
                                className="
                                    h-100
                                    border-0
                                    bg-dark
                                "
                            >

                                <Skeleton
                                    height="220px"
                                />

                                <Card.Body>

                                    <Skeleton
                                        width="45%"
                                        height="1rem"
                                        className="mb-3"
                                    />

                                    <Skeleton
                                        width="75%"
                                        height="1.7rem"
                                        className="mb-3"
                                    />

                                    <Skeleton
                                        width="95%"
                                        height="1rem"
                                        className="mb-2"
                                    />

                                    <Skeleton
                                        width="75%"
                                        height="1rem"
                                        className="mb-4"
                                    />

                                    <Skeleton
                                        width="100%"
                                        height="42px"
                                    />

                                </Card.Body>

                            </Card>

                        </Col>

                    )
                )}

            </Row>

        );

    };


    /* =====================================================
       NOT LOGGED IN
       ===================================================== */

    if (!isAuthenticated) {

        return (

            <Row className="mx-0 user-dash">

                <Col
                    xs={12}
                    className="
                        text-center
                        py-5
                        text-white
                    "
                >

                    <BsGift
                        size={55}
                        className="
                            mb-3
                            text-white
                        "
                    />


                    <h4 className="text-white">

                        Redeem Offers

                    </h4>


                    <p className="text-white">

                        Login to view available rewards.

                    </p>


                    <Button
                        label="Login"
                        className="pink_button"
                        onClick={() =>
                            navigate(
                                "/login"
                            )
                        }
                    />

                </Col>

            </Row>

        );

    }


    /* =====================================================
       RENDER
       ===================================================== */

    return (

        <Row
            className="
                mx-0
                user-dash
                text-white
                redeem-offers-page
            "
        >


            {/* =====================================================
                HEADER
                ===================================================== */}

            <Col
                xs={12}
                className="prof_title mb-4"
            >

                <div
                    className="
                        d-flex
                        align-items-start
                        justify-content-between
                        flex-wrap
                        gap-3
                    "
                >

                    <div>

                        <h4
                            className="
                                mb-1
                                text-white
                            "
                        >

                            Redeem Offers

                        </h4>


                        <p
                            className="
                                mb-0
                                text-white
                            "
                        >

                            Explore rewards across all subscription tiers.

                        </p>

                    </div>


                    {/* {hasActiveSubscription ? (

                        <div
                            className="
                                d-flex
                                gap-2
                                align-items-center
                                flex-wrap
                            "
                        >

                            <Badge
                                value={getTierLabel(
                                    customerTier
                                )}
                                severity="warning"
                            />


                            {subscriptionName && (

                                <span
                                    className="
                                        small
                                        text-white
                                    "
                                >

                                    {
                                        subscriptionName
                                    }

                                </span>

                            )}

                        </div>

                    ) : (

                        <Badge
                            value="No Active Subscription"
                            severity="danger"
                        />

                    )} */}

                </div>

            </Col>


            {/* =====================================================
                NO ACTIVE SUBSCRIPTION
                ===================================================== */}

            {!loading &&
                !hasActiveSubscription && (

                <Col
                    xs={12}
                    className="mb-4"
                >

                    <Card
                        className="
                            border
                            border-secondary
                            bg-dark
                            p-3
                        "
                    >

                        <Card.Body
                            className="
                                d-flex
                                align-items-center
                                justify-content-between
                                gap-3
                                flex-wrap
                                text-white
                            "
                        >

                            <div>

                                <h5
                                    className="
                                        mb-1
                                        text-white
                                    "
                                >

                                    Subscriber Rewards

                                </h5>


                                <p
                                    className="
                                        mb-0
                                        text-white
                                    "
                                >

                                    You can explore all rewards, but an active subscription is required to redeem them.

                                </p>

                            </div>


                            <Button
                                label="View Subscription Plans"
                                className="pink_button"
                                onClick={() =>
                                    navigate(
                                        "/packages"
                                    )
                                }
                            />

                        </Card.Body>

                    </Card>

                </Col>

            )}


            {/* =====================================================
                LOADING
                ===================================================== */}

            {loading && (

                <Col xs={12}>

                    <OfferSkeleton />

                </Col>

            )}


            {/* =====================================================
                NO OFFERS
                ===================================================== */}

            {!loading &&
                offers.length === 0 && (

                <Col xs={12}>

                    <Card
                        className="
                            border
                            border-secondary
                            bg-dark
                            text-center
                            py-5
                            text-white
                        "
                    >

                        <Card.Body>

                            <BsGift
                                size={48}
                                className="
                                    mb-3
                                    text-white
                                "
                            />


                            <h5 className="text-white">

                                No Offers Available

                            </h5>


                            <p
                                className="
                                    text-white
                                    mb-0
                                "
                            >

                                There are currently no active redeem offers.

                            </p>

                        </Card.Body>

                    </Card>

                </Col>

            )}


            {/* =====================================================
                OFFERS
                ===================================================== */}

            {!loading &&
                offers.length > 0 && (

                <Col xs={12} className="p-0">

                    <Row className="g-4">

                        {offers.map(
                            (offer) => {

                                const givesCoins =
                                    offer.offer_type ===
                                        "COINS" ||
                                    offer.offer_type ===
                                        "COINS_AND_ZIP";


                                const givesZip =
                                    offer.offer_type ===
                                        "ZIP" ||
                                    offer.offer_type ===
                                        "COINS_AND_ZIP";


                                const redeemed =
                                    Boolean(
                                        offer.already_redeemed
                                    );


                                const offerTier =
                                    Number(
                                        offer.applicable_tier
                                    );


                                const currentTier =
                                    Number(
                                        customerTier ??
                                        tierlevel
                                    );


                                const tierMatches =
                                    hasActiveSubscription &&
                                    Number.isFinite(
                                        currentTier
                                    ) &&
                                    offerTier ===
                                        currentTier;


                                const eligible =
                                    hasActiveSubscription &&
                                    tierMatches &&
                                    !redeemed &&
                                    offer.can_redeem !== false;


                                return (

                                    <Col
                                        xl={4}
                                        lg={4}
                                        md={6}
                                        xs={12}
                                        key={
                                            offer.redeem_offer_id
                                        }
                                    >

                                        <Card
                                            className="
                                                h-100
                                                border
                                                border-secondary
                                                bg-dark
                                                overflow-hidden
                                                redeem-offer-card
                                                text-white
                                            "
                                        >


                                            {/* =================================
                                                IMAGE
                                                ================================= */}

                                            <div
                                                className="
                                                    position-relative
                                                "
                                                style={{
                                                    minHeight:
                                                        "220px",

                                                    background:
                                                        "#f7f7f7",
                                                }}
                                            >

                                                {offer.thumbnail_url ? (

                                                    <img
                                                        src={
                                                            offer.thumbnail_url
                                                        }

                                                        alt={
                                                            offer.title
                                                        }

                                                        style={{
                                                            width:
                                                                "100%",

                                                            height:
                                                                "220px",

                                                            objectFit:
                                                                "cover",
                                                        }}
                                                    />

                                                ) : (

                                                    <div
                                                        className="
                                                            d-flex
                                                            align-items-center
                                                            justify-content-center
                                                        "
                                                        style={{
                                                            height:
                                                                "220px",
                                                        }}
                                                    >

                                                        <BsGift
                                                            size={60}
                                                            style={{
                                                                opacity:
                                                                    0.25,
                                                            }}
                                                        />

                                                    </div>

                                                )}


                                                {/* OFFER TYPE */}

                                                <div
                                                    className="
                                                        position-absolute
                                                        top-0
                                                        start-0
                                                        m-3
                                                    "
                                                >

                                                    <Badge
                                                        value={
                                                            offerTypeLabel(
                                                                offer.offer_type
                                                            )
                                                        }

                                                        severity={
                                                            offerTypeSeverity(
                                                                offer.offer_type
                                                            )
                                                        }
                                                    />

                                                </div>


                                                {/* PLAN */}

                                                <div
                                                    className="
                                                        position-absolute
                                                        bottom-0
                                                        start-0
                                                        m-3
                                                    "
                                                >

                                                    <Badge
                                                        value={
                                                            getTierLabel(
                                                                offer.applicable_tier
                                                            )
                                                        }

                                                        severity={
                                                            tierMatches
                                                                ? "success"
                                                                : "secondary"
                                                        }
                                                    />

                                                </div>


                                                {/* REDEEMED */}

                                                {redeemed && (

                                                    <div
                                                        className="
                                                            position-absolute
                                                            top-0
                                                            end-0
                                                            m-3
                                                        "
                                                    >

                                                        <Badge
                                                            value="Redeemed"
                                                            severity="success"
                                                        />

                                                    </div>

                                                )}

                                            </div>


                                            {/* =================================
                                                CONTENT
                                                ================================= */}

                                            <Card.Body
                                                className="
                                                    d-flex
                                                    flex-column
                                                    text-white
                                                "
                                            >

                                                <div>

                                                    <div
                                                        className="
                                                            small
                                                            text-white
                                                            mb-2
                                                        "
                                                    >
                                                        {
                                                            getTierLabel(
                                                                offer.applicable_tier
                                                            )
                                                        } Reward
                                                    </div>


                                                    <h5
                                                        className="
                                                            mb-2
                                                            text-white
                                                        "
                                                    >

                                                        {
                                                            offer.title
                                                        }

                                                    </h5>


                                                    {offer.description && (

                                                        <p
                                                            className="
                                                                text-white
                                                                mb-0
                                                            "
                                                            style={{
                                                                fontSize:
                                                                    "14px",
                                                            }}
                                                        >

                                                            {
                                                                offer.description
                                                            }

                                                        </p>

                                                    )}

                                                </div>


                                                {/* =================================
                                                    REWARD DETAILS
                                                    ================================= */}

                                                <div
                                                    className="
                                                        d-flex
                                                        flex-column
                                                        gap-2
                                                        mb-4
                                                        text-white
                                                        mt-0
                                                    "
                                                >


                                                    {givesCoins && (

                                                        <div
                                                            className="
                                                                d-flex
                                                                align-items-center
                                                                gap-2
                                                                text-white
                                                            "
                                                        >

                                                            <GiTwoCoins
                                                                size={22}
                                                            />


                                                            <span className="text-white">

                                                                <b className="text-white">

                                                                    {
                                                                        offer.coin_amount
                                                                    }

                                                                </b>

                                                                {" "}

                                                                Credit Coins

                                                            </span>

                                                        </div>

                                                    )}


                                                    {givesZip && (

                                                        <div
                                                            className="
                                                                d-flex
                                                                align-items-center
                                                                gap-2
                                                                text-white
                                                            "
                                                        >

                                                            <MdOutlineFileDownload
                                                                size={22}
                                                            />


                                                            <span className="text-white">

                                                                Downloadable Asset Pack

                                                            </span>

                                                        </div>

                                                    )}


                                                    <div
                                                        className="
                                                            small
                                                            text-white
                                                            mt-1
                                                        "
                                                    >

                                                        Valid until:{" "}

                                                        <b className="text-white">

                                                            {
                                                                formatExpiry(
                                                                    offer.expires_at
                                                                )
                                                            }

                                                        </b>

                                                    </div>

                                                </div>


                                                {/* =================================
                                                    BUTTON
                                                    ================================= */}

                                                <div className="mt-auto">


                                                    {redeemed ? (

                                                        givesZip ? (

                                                            <Button
                                                                label="Download Again"
                                                                icon="pi pi-download"
                                                                outlined
                                                                className="w-100 py-2"

                                                                loading={
                                                                    downloadingId ===
                                                                    offer.redeem_offer_id
                                                                }

                                                                disabled={
                                                                    downloadingId !==
                                                                    null
                                                                }

                                                                onClick={() =>
                                                                    downloadRedeemedZip(
                                                                        offer
                                                                    )
                                                                }
                                                            />

                                                        ) : (

                                                            <Button
                                                                label="Redeemed"
                                                                icon="pi pi-check"
                                                                severity="success"
                                                                outlined
                                                                disabled
                                                                className="w-100 py-2"
                                                            />

                                                        )

                                                    ) : !hasActiveSubscription ? (

                                                        <Button
                                                            label="Subscription Required"
                                                            icon="pi pi-lock"
                                                            severity="secondary"
                                                            outlined
                                                            disabled
                                                            className="w-100 py-2"
                                                        />

                                                    ) : !tierMatches ? (

                                                        <Button
                                                            label={`${getTierName(
                                                                offer.applicable_tier
                                                            )} Only`}
                                                            icon="pi pi-lock"
                                                            severity="secondary"
                                                            outlined
                                                            disabled
                                                            className="w-100 py-2"
                                                        />

                                                    ) : (

                                                        <Button
                                                            label="Redeem Offer"
                                                            icon="pi pi-gift"

                                                            className="
                                                                pink_button
                                                                w-100
                                                                py-2
                                                            "

                                                            loading={
                                                                redeemingId ===
                                                                offer.redeem_offer_id
                                                            }

                                                            disabled={
                                                                redeemingId !==
                                                                    null ||
                                                                !eligible
                                                            }

                                                            onClick={() =>
                                                                redeemOffer(
                                                                    offer
                                                                )
                                                            }
                                                        />

                                                    )}

                                                </div>

                                            </Card.Body>

                                        </Card>

                                    </Col>

                                );

                            }
                        )}

                    </Row>

                </Col>

            )}

        </Row>

    );

};


export default RedeemOffers;